// ============================================================================
// i18n State - Client-side internationalization with reactive signals
// ============================================================================

import type { ItemTypeValue, NpcTypeValue, ObjectTypeValue } from '@dreamgame/shared';
import {
  en,
  getNestedValue,
  type ItemTranslations,
  interpolate,
  type NpcTranslations,
  type ObjectTranslations,
  type RecipeTranslations,
  type SkillTranslations,
  type TranslationKey,
  type Translations,
  toTranslationKey,
} from '@dreamgame/shared';
import { signal } from '@preact/signals';

// ============================================================================
// State
// ============================================================================

// Current language code
export const currentLanguage = signal<string>('en');

// Current translations object
export const translations = signal<Translations>(en);

// ============================================================================
// Core Translation Function
// ============================================================================

/**
 * Translate a key to a localized string.
 * Supports dot notation for nested keys and interpolation.
 * Type-safe: only valid translation keys are accepted (compile-time check).
 *
 * @example
 * t('ui.panels.build') // => "Build Menu"
 * t('ui.labels.position', { x: 10, y: 20 }) // => "Position: (10, 20)"
 * t('ui.empty.noItemsMatch', { query: 'wood' }) // => "No items match "wood""
 * t('invalid.key') // TypeScript error!
 */
export const t = (key: TranslationKey, params?: Record<string, string | number>): string => {
  const value = getNestedValue({
    obj: translations.value as unknown as Record<string, unknown>,
    path: key,
  });

  if (value === undefined) {
    // Return the key itself if translation is missing (useful for debugging)
    console.warn(`[i18n] Missing translation key: ${key}`);
    return key;
  }

  if (params) {
    return interpolate({ text: value, params });
  }

  return value;
};

// ============================================================================
// Type-Safe Game Data Helpers
// ============================================================================

// Map from ItemType enum key to translation key
const ITEM_TYPE_KEYS: Record<number, keyof ItemTranslations> = {
  0: 'none',
  1: 'wood',
  2: 'stone',
  3: 'ironOre',
  4: 'goldOre',
  5: 'copperOre',
  6: 'coal',
  100: 'plank',
  101: 'ironBar',
  102: 'goldBar',
  103: 'copperBar',
  104: 'steelBar',
  200: 'berries',
  201: 'rawMeat',
  202: 'cookedMeat',
  203: 'bread',
  300: 'woodenAxe',
  301: 'stoneAxe',
  302: 'ironAxe',
  310: 'woodenPickaxe',
  311: 'stonePickaxe',
  312: 'ironPickaxe',
  400: 'woodenSword',
  401: 'stoneSword',
  402: 'ironSword',
};

// Map from ObjectType enum value to translation key
const OBJECT_TYPE_KEYS: Record<number, keyof ObjectTranslations> = {
  0: 'none',
  1: 'treeOak',
  2: 'treePine',
  3: 'treeBirch',
  10: 'bush',
  20: 'rockSmall',
  21: 'rockMedium',
  22: 'rockLarge',
  23: 'boulder',
  100: 'wallWood',
  101: 'wallStone',
  102: 'wallMetal',
  110: 'doorWood',
  111: 'doorMetal',
  120: 'floorWood',
  121: 'floorStone',
  200: 'bed',
  201: 'table',
  202: 'chair',
  203: 'sleepingPad',
  210: 'storageCrate',
  211: 'storageShelf',
  300: 'craftingTable',
  301: 'anvil',
  302: 'campfire',
  303: 'cookingStove',
  304: 'furnace',
  400: 'oreIron',
  401: 'oreGold',
  402: 'oreCopper',
  500: 'claimFlagHome',
  501: 'claimFlagOutpost',
};

// Map from NpcType enum value to translation key
const NPC_TYPE_KEYS: Record<number, keyof NpcTranslations> = {
  1: 'rabbit',
  2: 'squirrel',
  3: 'mouse',
  4: 'rat',
  10: 'deer',
  11: 'boar',
  12: 'goat',
  13: 'sheep',
  20: 'horse',
  21: 'cow',
  22: 'moose',
  23: 'bison',
  30: 'fox',
  31: 'wildcat',
  32: 'badger',
  33: 'weasel',
  40: 'wolf',
  41: 'coyote',
  42: 'lynx',
  43: 'hyena',
  50: 'bear',
  51: 'lion',
  52: 'tiger',
  53: 'cougar',
  60: 'chicken',
  61: 'turkey',
  62: 'pheasant',
  63: 'duck',
  64: 'goose',
  70: 'hawk',
  71: 'eagle',
  72: 'owl',
  73: 'raven',
  80: 'snake',
  81: 'lizard',
  82: 'turtle',
  83: 'crocodile',
  90: 'fish',
  91: 'salmon',
  92: 'crab',
  93: 'frog',
  100: 'bee',
  101: 'spider',
  102: 'scorpion',
};

// Map from skill ID to translation key
const SKILL_ID_KEYS: Record<string, keyof SkillTranslations> = {
  melee: 'melee',
  shooting: 'shooting',
  toughness: 'toughness',
  mining: 'mining',
  woodcutting: 'woodcutting',
  farming: 'farming',
  animals: 'animals',
  fishing: 'fishing',
  metalwork: 'metalwork',
  textiles: 'textiles',
  mechanics: 'mechanics',
  construction: 'construction',
  cooking: 'cooking',
  medicine: 'medicine',
  science: 'science',
  research: 'research',
  art: 'art',
  trading: 'trading',
  leadership: 'leadership',
  teaching: 'teaching',
};

// Map from recipe ID to translation key (snake_case to camelCase)
const recipeIdToKey = (recipeId: string): keyof RecipeTranslations => {
  return toTranslationKey({ enumKey: recipeId }) as keyof RecipeTranslations;
};

/**
 * Get localized item name
 */
export const itemName = ({ itemType }: { itemType: ItemTypeValue }): string => {
  const key = ITEM_TYPE_KEYS[itemType];
  if (!key) {
    return translations.value.items.none;
  }
  return translations.value.items[key];
};

/**
 * Get localized object name
 */
export const objectName = ({ objectType }: { objectType: ObjectTypeValue }): string => {
  const key = OBJECT_TYPE_KEYS[objectType];
  if (!key) {
    return translations.value.objects.none;
  }
  return translations.value.objects[key];
};

/**
 * Get localized animal name
 */
export const npcName = ({ npcType }: { npcType: NpcTypeValue }): string => {
  const key = NPC_TYPE_KEYS[npcType];
  if (!key) {
    return 'Unknown';
  }
  return translations.value.npcs[key];
};

/**
 * Get localized skill name
 */
export const skillName = ({ skillId }: { skillId: string }): string => {
  const key = SKILL_ID_KEYS[skillId];
  if (!key) {
    return skillId;
  }
  return translations.value.skills[key];
};

/**
 * Get localized recipe name
 */
export const recipeName = ({ recipeId }: { recipeId: string }): string => {
  const key = recipeIdToKey(recipeId);
  const recipe = translations.value.recipes[key];
  if (!recipe) {
    return recipeId;
  }
  return recipe.name;
};

/**
 * Get localized recipe description
 */
export const recipeDescription = ({ recipeId }: { recipeId: string }): string => {
  const key = recipeIdToKey(recipeId);
  const recipe = translations.value.recipes[key];
  if (!recipe) {
    return '';
  }
  return recipe.desc;
};

// ============================================================================
// Language Management
// ============================================================================

/**
 * Set the current language.
 * Currently only supports 'en', but structured for future expansion.
 */
export const setLanguage = ({ language }: { language: string }): void => {
  if (language === 'en') {
    currentLanguage.value = 'en';
    translations.value = en;
  } else {
    console.warn(`[i18n] Language '${language}' not supported, falling back to 'en'`);
    currentLanguage.value = 'en';
    translations.value = en;
  }
};
